<template>
    <div class="group">
        <div class="group_table">
            <div class="group_tableHeader">
                <div class="group_tableRow">
                    <div class="group_th group_th-title">
                        {{ group.groupName }}
                    </div>
                    <div
                        v-for="column in columns"
                        v-bind:key="column.key"
                        class="group_th"
                        v-bind:class="{
                            'group_th-pts': column.key === GroupTableColumn.Points,
                            'group_th-odds': column.key === GroupTableColumn.Winner,
                        }"
                    >
                        {{ column.title }}
                    </div>
                </div>
            </div>
            <div class="group_tableBody">
                <div
                    v-for="team in group.teamList"
                    v-bind:key="team.id"
                    class="group_tableRow group_row"
                >
                    <div class="group_teamInfo">
                        <div class="group_rank">
                            {{ team.ranking }}
                        </div>
                        <div class="group_iconCell">
                            <picture>
                                <source
                                    v-bind:srcset="`${team.logo.webp}`"
                                    type="image/webp"
                                    class="teamSymbolImg"
                                >
                                <img
                                    v-bind:src="`${team.logo.origin}`"
                                    alt=""
                                    aria-hidden="true"
                                    class="teamSymbolImg"
                                >
                            </picture>
                        </div>
                        <div class="group_teamName">
                            {{ team.name }}
                        </div>
                    </div>
                    <template
                        v-for="column in columns"
                        v-bind:key="column.key"
                    >
                        <div
                            v-if="column.key === GroupTableColumn.Winner"
                            class="group_td group_td-odds"
                        >
                            <template v-if="isOutrightActive">
                                <div v-if="!isOutrightLoaded" class="skeletonContainer">
                                    <Skeleton widthType="half" />
                                </div>
                                <OutrightOdds
                                    v-else-if="matchedOutrights[team.countryName] && outrightLeague"
                                    v-bind:league="outrightLeague"
                                    v-bind:oddsInfo="matchedOutrights[team.countryName]"
                                    v-bind:displaySection="DisplaySection.TournamentCenter_Groups"
                                />
                            </template>
                        </div>
                        <div
                            v-else
                            class="group_td"
                            v-bind:class="{
                                'group_td-pts': column.key === GroupTableColumn.Points,
                            }"
                        >
                            {{ team.valueMapping[column.key] }}
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed, toRefs } from 'vue';
    import Skeleton from '@/components/common/skeleton/Skeleton.vue';
    import OutrightOdds from '@/components/odds/OutrightOdds.vue';
    import { useOutrightOdds } from '@/core/oddsApi/composable/useOutrightOdds';
    import type { IOutrightLeague } from '@/interface/IOutrightLeague';
    import { DisplaySection, GroupTableColumn, SportType } from '@/interface/enum';
    import type { IGroupColumn, IGroupDisplay } from '@/interface/tournamentCenter';

    const props = defineProps<{
        group: IGroupDisplay;
        columns: IGroupColumn[];
        outrightLeague: IOutrightLeague | null;
        outrightLeagueLoaded: boolean;
    }>();

    const { outrightLeague, outrightLeagueLoaded } = toRefs(props);

    const outrightLeagueId = computed(() => outrightLeague.value?.id ?? 0);
    const isOutrightActive = computed(() => outrightLeagueLoaded.value && !!outrightLeague.value);

    const { outrightOdds, loaded: outrightOddsLoaded } = useOutrightOdds(
        SportType.Soccer,
        outrightLeagueId,
        false,
        isOutrightActive,
    );

    const isOutrightLoaded = computed(() => outrightLeagueLoaded.value && outrightOddsLoaded.value);

    const matchedOutrights = computed(() => {
        const oddsByCountry = outrightOdds.value.reduce<Record<string, typeof outrightOdds.value[number]>>((acc, odds) => {
            if (odds.countryName) {
                acc[odds.countryName.toLowerCase()] = odds;
            }
            return acc;
        }, {});

        return props.group.teamList.reduce<Record<string, typeof outrightOdds.value[number]>>((result, team) => {
            if (team.countryName) {
                const matched = oddsByCountry[team.countryName.toLowerCase()];
                if (matched) {
                    result[team.countryName] = matched;
                }
            }
            return result;
        }, {});
    });
</script>

<style lang="scss" scoped>
    @import "@/components/tournamentCenter/Group";
</style>
